import { SchoolModel, UserProfileType } from '@peekapak/types/DataProtocolTypes';
import { getLocalForage, setLocalForage } from '../../aaa';
import { getJob, getUsers, queryMixpanelByGroupV2 } from '../../BackendInterface';
import { countAssignmentsInRange, loadAssignmentsMap, loadTeacherMaps } from '../SchoolReport/schoolRosterData';
import {
  DistrictPdfAggregateRow,
  DistrictPdfAggregates,
  emptyDistrictPdfAggregates,
  getDistrictPdfAggregatesScript,
  getDistrictPdfQueryRange,
  parseDistrictPdfAggregates,
} from './districtReportDeck';

const tableOverrideSuffix = '';

// Same TTL as the school report's own PDF-only mood query - long enough to make repeat downloads
// instant, short enough that today's check-ins show up within the hour.
const AGGREGATES_TTL_MINUTES = 60;
// Shared with SchoolReport/MoodboardSchoolAdmin, which cache the same per-school user list under
// the same key and TTL - so a school visited in either place is already warm here, and vice versa.
const USERS_TTL_MINUTES = 10080;
const USER_LOOKUP_CONCURRENCY = 4;
// The users job can legitimately take minutes for a large school (the school report tells users
// loading "may take up to 10 minutes or more"), so the cap is generous - it only exists so a job
// that never finishes fails the PDF instead of leaving its spinner up forever.
const USERS_JOB_POLL_INTERVAL_MS = 1000;
const USERS_JOB_MAX_POLLS = 600;

export async function loadDistrictPdfAggregates(
  districtId: string,
  schoolList: SchoolModel[],
  startDate: Date,
  endDate: Date,
): Promise<DistrictPdfAggregates> {
  const range = getDistrictPdfQueryRange(startDate, endDate);
  const schools = schoolList.map((school) => school.combineIds || school.id);
  if (!range || !schools.length) return emptyDistrictPdfAggregates();

  // The school ids are part of the key so a change to the district's school list isn't masked by
  // an older cached result.
  const storageKey = `${districtId}-districtPdfAggregates-v2-from-${range.from}-to-${range.to}-${schools.flat().join(',')}`;
  const cached = await getLocalForage(storageKey);
  if (cached) return cached as DistrictPdfAggregates;

  const rows = (await queryMixpanelByGroupV2(
    getDistrictPdfAggregatesScript(range.from, range.to, schools),
    'unified',
    tableOverrideSuffix,
  )) as unknown as DistrictPdfAggregateRow[];
  const aggregates = parseDistrictPdfAggregates(rows);
  await setLocalForage(storageKey, aggregates, AGGREGATES_TTL_MINUTES);
  return aggregates;
}

// Mirrors SchoolReport's getUsersInSchool: kick off the async users job, then poll it.
export async function getSchoolUsers(schoolId: string): Promise<UserProfileType[]> {
  const storageKey = `${schoolId}-users`;
  const cached = await getLocalForage(storageKey);
  if (cached) return cached as UserProfileType[];

  const jobId = await getUsers(schoolId, tableOverrideSuffix);
  let job = await getJob(jobId);
  let polls = 0;
  while (job.status !== 'Done' && job.status !== 'Error') {
    if (++polls > USERS_JOB_MAX_POLLS) throw new Error(`Timed out loading users for school ${schoolId}`);
    await new Promise((resolve) => setTimeout(resolve, USERS_JOB_POLL_INTERVAL_MS));
    job = await getJob(jobId);
  }
  if (job.status === 'Error') {
    const message = job.data ? JSON.parse(job.data).message : undefined;
    throw new Error(message || `Unable to load users for school ${schoolId}`);
  }

  const users = JSON.parse(job.data) as UserProfileType[];
  await setLocalForage(storageKey, users, USERS_TTL_MINUTES);
  return users;
}

// userId -> "First Last" for every user in the given schools. Only called when the PDF is actually
// requested (one users job per school is far too much to run on every district page view), and
// only for schools that have educator logins in the range. A school whose lookup fails just ends
// up with no named educators rather than failing the whole PDF.
export async function loadEducatorNames(
  schools: SchoolModel[],
  onProgress?: (done: number, total: number) => void,
): Promise<Record<string, string>> {
  const schoolIds = [
    ...new Set(schools.flatMap((school) => (school.combineIds?.length ? school.combineIds : [school.id]))),
  ];
  const names: Record<string, string> = {};
  let done = 0;
  let next = 0;

  const worker = async () => {
    while (next < schoolIds.length) {
      const schoolId = schoolIds[next++];
      try {
        const users = await getSchoolUsers(schoolId);
        users.forEach((user) => {
          if (user.type === 'Student' || !user.userId) return;
          const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
          if (name) names[user.userId] = name;
        });
      } catch (error) {
        console.error(`Failed to load educator names for school ${schoolId}`, error);
      }
      done += 1;
      onProgress?.(done, schoolIds.length);
    }
  };

  await Promise.all(Array.from({ length: Math.min(USER_LOOKUP_CONCURRENCY, schoolIds.length) }, worker));
  return names;
}

const ASSIGNMENT_SCHOOL_CONCURRENCY = 3;
const ASSIGNMENT_STUDENT_CONCURRENCY = 4;

// The PDF's "Assignments" number: for every school on the district's list, the exact count that
// school's own report produces - its users, then its teachers' rosters, then every grade 6+ student's
// assignments (see schoolRosterData.ts, which the school report uses too), counted over the picked
// range - added up. A school whose data can't be loaded fails the whole count rather than silently
// leaving it out.
export async function loadDistrictAssignmentsCount(
  schools: SchoolModel[],
  fetchWindow: { start: string; end: string },
  from: Date,
  to: Date,
): Promise<number> {
  let total = 0;
  let next = 0;
  const worker = async () => {
    while (next < schools.length) {
      const school = schools[next++];
      const users = await getSchoolUsers(school.id);
      const { teacherStudentMap } = await loadTeacherMaps(school.id, users, fetchWindow.start, fetchWindow.end);
      const assignmentsMap = await loadAssignmentsMap(
        school.id,
        teacherStudentMap,
        fetchWindow.start,
        fetchWindow.end,
        {
          concurrency: ASSIGNMENT_STUDENT_CONCURRENCY,
        },
      );
      total += countAssignmentsInRange(assignmentsMap || {}, from, to);
    }
  };
  await Promise.all(Array.from({ length: Math.min(ASSIGNMENT_SCHOOL_CONCURRENCY, schools.length) }, worker));
  return total;
}
